import { Link } from "react-router-dom";
import { useMemo } from "react";
import { ArrowRight, Mic, ShieldCheck } from "lucide-react";
import AnimatedHeroDemo from "@/components/marketing/AnimatedHeroDemo";
import { cta } from "./_shared";
import { pickVariant } from "@/lib/abtest";

const STATS = [
  { stat: "2 min", sub: "to file a shift note" },
  { stat: "1 afternoon", sub: "to go live — no IT team" },
  { stat: "$180/mo", sub: "starting price · replaces 3 tools" },
];

/**
 * Hero headline A/B test — `hero_headline_v1`.
 *
 *  control  — current category-defining headline. Speaks to the agency owner.
 *  two_min  — outcome-first variant. Speaks to the doer who needs the result.
 *
 * Track which variant converts better via PostHog: every event fired after
 * mount auto-carries `experiment_hero_headline_v1: <variant>` (registered as
 * a super-property), so any insight can be sliced by variant.
 */
const HEADLINES = {
  control: {
    line1: "Run your home care agency,",
    line2: "not your spreadsheets.",
    sub: "Scheduling, voice-dictated shift notes, EVV clock-in, payroll, and family updates — in one platform built for small home-care agencies.",
  },
  two_min: {
    line1: "Two-minute shift notes.",
    line2: "Audit-ready in seconds.",
    sub: "Caregivers speak. Afyanex writes the chart, clocks the visit, and updates the family — without a single spreadsheet.",
  },
};

/**
 * Primary CTA copy A/B test — `hero_cta_v1`.
 * Three-way split — `pickVariant` distributes equally by default.
 */
const PRIMARY_CTA = {
  demo:   "Try the live demo",
  trial:  "Start your free trial",
  sixty:  "Create your agency — 60 seconds",
};

export default function LandingHero() {
  // Pick + register the variants once per render (deterministic per visitor).
  const variant = useMemo(() => pickVariant("hero_headline_v1", ["control", "two_min"]), []);
  const ctaVariant = useMemo(() => pickVariant("hero_cta_v1", ["demo", "trial", "sixty"]), []);
  const copy = HEADLINES[variant] || HEADLINES.control;
  const primaryCtaLabel = PRIMARY_CTA[ctaVariant] || PRIMARY_CTA.demo;
  return (
    <>
      {/* Hero */}
      <section className="relative overflow-hidden" data-testid={`landing-hero-${variant}`}>
        <div aria-hidden className="pointer-events-none absolute -top-20 -right-20 h-[420px] w-[420px] rounded-full bg-afyanex-pink/15 blur-[120px]" />
        <div aria-hidden className="pointer-events-none absolute -top-10 -left-32 h-[380px] w-[380px] rounded-full bg-afyanex-cyan/20 blur-[120px]" />
        <div className="relative mx-auto max-w-6xl px-5 sm:px-8 pt-16 sm:pt-24 pb-16 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <h1 data-testid="landing-headline"
                className="font-heading text-[40px] sm:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.05] text-clinical-text">
              {copy.line1}
              <br />
              <span className="bg-gradient-to-r from-afyanex-blue via-[#7E47F2] to-afyanex-pink bg-clip-text text-transparent">
                {copy.line2}
              </span>
            </h1>
            <p data-testid="landing-subheadline"
               className="mt-6 text-base sm:text-lg text-clinical-subtext leading-relaxed max-w-xl">
              {copy.sub}
            </p>
            <div className="mt-5 text-sm text-clinical-text font-medium">
              Live in one afternoon. From <span className="text-afyanex-blue font-semibold">$180/mo</span>.
            </div>
            <div className="mt-7 flex flex-wrap items-center gap-3">
              <Link to="/agency/signup"
                    onClick={cta("hero_primary", "/agency/signup")}
                    data-testid="landing-cta-primary"
                    data-cta-variant={ctaVariant}
                    className="inline-flex items-center gap-2 rounded-full bg-afyanex-blue px-6 py-3.5 text-[15px] font-semibold text-white shadow-[0_12px_28px_-12px_rgba(0,94,255,0.55)] hover:shadow-[0_16px_36px_-12px_rgba(0,94,255,0.65)] hover:-translate-y-[1px] transition-all">
                {primaryCtaLabel} <ArrowRight className="h-4 w-4" />
              </Link>
              <Link to="/pricing"
                    onClick={cta("hero_secondary", "/pricing")}
                    data-testid="landing-cta-secondary"
                    className="inline-flex items-center gap-2 rounded-full border border-clinical-border bg-white px-6 py-3.5 text-[15px] font-semibold text-clinical-text hover:border-afyanex-blue/60 hover:text-afyanex-blue transition-all">
                Book a walkthrough
              </Link>
            </div>
            <div className="mt-7 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-clinical-subtext">
              <span className="inline-flex items-center gap-1.5">
                <ShieldCheck className="h-3.5 w-3.5 text-afyanex-blue" /> HIPAA-aligned · BAA on every plan
              </span>
              <span className="inline-flex items-center gap-1.5">
                <Mic className="h-3.5 w-3.5 text-afyanex-pink" /> No setup fees · Cancel anytime
              </span>
            </div>
          </div>
          <AnimatedHeroDemo />
        </div>
      </section>

      {/* 3-stat strip */}
      <section className="border-y border-clinical-border/60 bg-white">
        <div className="mx-auto max-w-5xl px-5 sm:px-8 py-12 grid sm:grid-cols-3 gap-y-8 gap-x-6 sm:divide-x sm:divide-clinical-border">
          {STATS.map((s, i) => (
            <div key={i} className="text-center sm:px-4">
              <div className="font-heading text-4xl sm:text-5xl font-semibold tracking-tight text-afyanex-blue">
                {s.stat}
              </div>
              <div className="mt-1.5 text-sm text-clinical-subtext">{s.sub}</div>
            </div>
          ))}
        </div>
      </section>
    </>
  );
}
